import React, { type FunctionComponent, useEffect, useState } from 'react'
import { spacing, Text, colors, Button } from '@retailer-platform/shared-components'
import { css } from '@emotion/react'
import { type FormContextValues } from 'react-hook-form'
import { MerchandisingFormSection } from 'domains/merchandising/components/form-section/MerchandisingFormSection'
import { MerchandisingFormRow } from 'domains/merchandising/components/form-row/MerchandisingFormRow'
import { MerchandisingFormField } from 'domains/merchandising/components/form-field/MerchandisingFormField'
import { MerchandisingFormSelect } from 'domains/merchandising/components/form-input/MerchandisingFormSelect'
import {
  bannerDestinationTypes,
  placementDestinationOptions,
} from 'domains/merchandising/constants/heroBanner.constants'
import { useHeroBannerFormLinkDestination } from 'domains/merchandising/utils/HeroBannerFormLinkDestination.hooks'
import { useRetailerId } from 'utils/routing/params.hooks'
import { Permission } from 'legacy/common/types'
import { Flipper } from 'legacy/contexts/flipper/FlipperContext'
import useAccessControl from 'legacy/components/AccessControl/useAccessControl'
import { useSelectOptionsDashMessage } from 'domains/merchandising/utils/intl.hooks'
import { MerchandisingFormInput } from 'domains/merchandising/components/form-input/MerchandisingFormInput'
import { useDashMessages } from 'intl/intl.hooks'
import { type I18nHeroBannerFormData } from 'domains/merchandising/types/heroBanner.types'
import { ErrorMessage } from 'gin-and-tonic/common/error-message/ErrorMessage'
import { MerchandisingFormSectionDivider } from 'domains/merchandising/components/form-section/MerchandisingFormSectionDivider'
import { generateDestinationUrl } from 'domains/merchandising/utils/heroBannerUtils'
import { localeIdentifier } from 'domains/merchandising/components/form-locale/MerchandisingFormLocale'
import { useIsI18nEnabled } from 'domains/merchandising/utils/useIsI18nEnabled.hooks'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'

interface Props {
  form: FormContextValues<I18nHeroBannerFormData>
  defaultDisplayValue: string
}

const labelStyles = css({
  margin: `auto ${spacing.X16} auto 0`,
  paddingTop: spacing.X16,
  color: colors.GRAYSCALE.X70,
})

const externalDescriptionStyles = css({
  color: colors.GRAYSCALE.X70,
  margin: 0,
})

const actionRowStyles = css({
  display: 'flex',
  justifyContent: 'flex-end',
})

export const HeroBannerFormLinkDestination: FunctionComponent<Props> = ({
  form,
  defaultDisplayValue,
}) => {
  const { watch, register, setValue } = form
  const destinationType: string = watch('destination.type') || null
  const destinationTarget: string = watch('destination.target') || ''

  const isExternal = destinationType === bannerDestinationTypes.external
  const isDepartment = destinationType === bannerDestinationTypes.department
  const isCollection = destinationType === bannerDestinationTypes.collection
  const isProduct = destinationType === bannerDestinationTypes.product

  const [defaultProductOption] = useState({
    label: defaultDisplayValue,
    value: destinationTarget,
  })

  const destinationOptions = useSelectOptionsDashMessage(placementDestinationOptions)

  const hasAccess = useAccessControl()
  const isAdmin = hasAccess({ permissions: [Permission.PromotedPlacementsManager] })
  const hasExternalUrlAccess = hasAccess({
    warehouseFlippers: Flipper.PromotedPlacementsExternalUrl,
  })

  const messages = useDashMessages({
    connectorLabel: 'common.is',
    externalDescription:
      'merchandising.hero-banners.section.link-destination.input.destinationTarget.external.description',
    testLink: 'merchandising.hero-banners.section.link-destination.testLink',
    noOptionsMessage:
      'merchandising.hero-banners.section.link-destination.input.destinationTarget.product.empty',
  })

  const retailerId = useRetailerId()
  const isI18nEnabled = useIsI18nEnabled()
  const { warehouse } = useWarehouseContext()

  const {
    retailerConfiguration,
    getDepartments,
    getCollections,
    getProductsByName,
    departmentOptions,
    collectionOptions,
    productOptions,
    loading,
    departmentsError,
    collectionsError,
    productsError,
  } = useHeroBannerFormLinkDestination({ retailerId })

  const testLink = generateDestinationUrl(destinationType, destinationTarget, retailerConfiguration)

  if (defaultDisplayValue) {
    productOptions.push(defaultProductOption)
  }

  const handleTargetInputChange = value => {
    if (value.length < 3) return

    getProductsByName({
      variables: {
        retailerId,
        localeCode: isI18nEnabled
          ? form.getValues(localeIdentifier)
          : warehouse.defaultLocale.localeCode.toUpperCase(),
        searchTerm: value,
        searchType: 'name',
        searchLimit: 25,
      },
    })
  }

  useEffect(() => {
    if (isDepartment) {
      getDepartments({ variables: { retailerId } })
    }
  }, [isDepartment, getDepartments, retailerId])

  useEffect(() => {
    if (isCollection) {
      getCollections({ variables: { retailerId } })
    }
  }, [isCollection, getCollections, retailerId])

  // At the moment, only admins can create external urls
  // If the user is not an admin, disable the external option and input
  // Being explicit here since this is hard to test
  if (isAdmin === false && hasExternalUrlAccess !== true) {
    const externalUrlIndex = destinationOptions.findIndex(
      option => option.value === bannerDestinationTypes.external
    )

    // If the user does not have access, remove the external option
    if (externalUrlIndex !== -1) {
      destinationOptions.splice(externalUrlIndex, 1)
    }

    // If the original destination type was external (edit form),
    // Keep the same destination type/target so the link
    // doesn't get removed on update
    if (destinationType === 'EXTERNAL') {
      return (
        <>
          <input type="hidden" value="EXTERNAL" name="destination.type" ref={register} />
          <input type="hidden" value={destinationTarget} name="destination.target" ref={register} />
        </>
      )
    }
  }

  return (
    <MerchandisingFormSection
      titleId="merchandising.hero-banners.section.link-destination.title"
      tooltipId="merchandising.hero-banners.section.link-destination.tooltip"
      renderAction
    >
      <input type="hidden" value={destinationTarget} name="destination.target" ref={register} />
      <MerchandisingFormRow>
        <MerchandisingFormField>
          <MerchandisingFormSelect
            labelId="merchandising.hero-banners.section.link-destination.input.destinationType.label"
            identifier="destination.type"
            form={form}
            onChange={() => setValue('destination.target', null)}
            options={destinationOptions}
            data-testid="destinationTypeSelect"
          />
        </MerchandisingFormField>

        {destinationType && <Text css={labelStyles}>{messages.connectorLabel}</Text>}

        {isExternal && (
          <MerchandisingFormField fullWidth>
            <MerchandisingFormInput
              labelId="merchandising.hero-banners.section.link-destination.input.destinationTarget.external"
              onChange={e => setValue('destination.target', e.target.value)}
              defaultValue={destinationTarget}
            />
          </MerchandisingFormField>
        )}

        {isDepartment && (
          <MerchandisingFormField>
            <MerchandisingFormSelect
              labelId="merchandising.hero-banners.section.link-destination.input.destinationTarget.department"
              onChange={({ value }) => setValue('destination.target', value)}
              defaultValue={destinationTarget}
              options={departmentOptions}
              loading={loading}
            />
          </MerchandisingFormField>
        )}

        {isCollection && (
          <MerchandisingFormField>
            <MerchandisingFormSelect
              labelId="merchandising.hero-banners.section.link-destination.input.destinationTarget.collection"
              onChange={({ value }) => setValue('destination.target', value)}
              defaultValue={destinationTarget}
              options={collectionOptions}
              loading={loading}
              data-testid="collectionSelect"
            />
          </MerchandisingFormField>
        )}

        {isProduct && (
          <MerchandisingFormField>
            <MerchandisingFormSelect
              labelId="merchandising.hero-banners.section.link-destination.input.destinationTarget.product"
              onChange={({ value }) => setValue('destination.target', value)}
              options={productOptions}
              isLoading={loading}
              onInputChange={handleTargetInputChange}
              filterOption={() => true}
              noOptionsMessage={() => messages.noOptionsMessage}
              defaultValue={destinationTarget}
            />
          </MerchandisingFormField>
        )}
      </MerchandisingFormRow>

      {isExternal && (
        <Text css={externalDescriptionStyles} size="small">
          {messages.externalDescription}
        </Text>
      )}

      {isDepartment && departmentsError && (
        <ErrorMessage
          messageId={
            'merchandising.hero-banners.section.link-destination.input.destinationTarget.departments.error'
          }
        />
      )}

      {isCollection && collectionsError && (
        <ErrorMessage
          messageId={
            'merchandising.hero-banners.section.link-destination.input.destinationTarget.collections.error'
          }
        />
      )}

      {isProduct && productsError && (
        <ErrorMessage
          messageId={
            'merchandising.hero-banners.section.link-destination.input.destinationTarget.products.error'
          }
        />
      )}

      <MerchandisingFormSectionDivider />

      <div css={actionRowStyles}>
        <a target="_blank" rel="noreferrer" href={testLink}>
          <Button disabled={!testLink} variant="secondary">
            {messages.testLink}
          </Button>
        </a>
      </div>
    </MerchandisingFormSection>
  )
}
